import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";
import { Estacion, Ubicacion } from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";
import { soloFecha, soloHora } from "../formato-fecha";

/**
 * Estaciones de kiosco.
 *
 * Un kiosco no tiene sesión: opera desatendido y sincroniza su cola sin
 * conexión mucho después de que caduque la sesión de quien lo dio de
 * alta. Su identidad es una credencial que se emite desde aquí.
 *
 * LA CREDENCIAL SE MUESTRA UNA SOLA VEZ
 * En base solo queda su hash, así que ni un volcado de la tabla permite
 * suplantar a una estación — y tampoco hay forma de recuperarla. Si el
 * operador no la copia, hay que emitir otra y revocar ésta. La pantalla
 * lo dice antes de emitir, no después.
 */
@Component({
  selector: "app-estaciones",
  standalone: true,
  templateUrl: "./estaciones.component.html",
  styleUrls: ["./estaciones.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class EstacionesComponent implements OnInit {
  estaciones: Estacion[] = [];
  ubicaciones: Ubicacion[] = [];
  loading = false;

  /** Formulario de emisión. */
  nueva: Estacion = new Estacion();

  /**
   * La credencial recién emitida. Vive solo en memoria y solo hasta que
   * el operador la descarta: no se vuelve a pedir al servidor porque el
   * servidor tampoco la tiene.
   */
  credencialEmitida: Estacion = null;
  copiada = false;

  constructor(
    private asistenciaService: AsistenciaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarUbicaciones();
    queueMicrotask(() => {
      this.consultar();
      this.cdr.detectChanges();
    });
  }

  consultar(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarEstaciones()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          this.estaciones = respuesta?.list ?? [];
        },
        error: () => {
          this.estaciones = [];
          this.msg.errorCall("No se pudieron consultar las estaciones.", null, null);
        },
      });
  }

  emitir(): void {
    if (!this.nueva.idUbicacion) {
      this.msg.warningCall("Elija el sitio al que servirá esta estación.", null, null);
      return;
    }
    if (!this.nueva.nombre?.trim()) {
      this.msg.warningCall("Póngale un nombre al equipo, para poder reconocerlo después.", null, null);
      return;
    }

    this.msg.questionCall(
      "La credencial se muestra una sola vez y no se puede recuperar. ¿Emitirla ahora?",
      null,
      (respuesta: any) => {
        if (!respuesta?.value) {
          return;
        }
        this.asistenciaService.emitirEstacion(this.nueva).subscribe({
          next: (emitida) => {
            if (!emitida?.tokenEnClaro) {
              this.msg.warningCall(emitida?.nombre || "No se pudo emitir la credencial.", null, null);
              return;
            }
            this.credencialEmitida = emitida;
            this.copiada = false;
            this.nueva = new Estacion();
            this.consultar();
          },
          error: () => this.msg.errorCall("No se pudo emitir la credencial.", null, null),
        });
      }
    );
  }

  copiarCredencial(): void {
    const token = this.credencialEmitida?.tokenEnClaro;
    if (!token) {
      return;
    }
    navigator.clipboard.writeText(token).then(
      () => {
        this.copiada = true;
        this.cdr.detectChanges();
      },
      () => this.msg.warningCall("No se pudo copiar. Selecciónela y cópiela a mano.", null, null)
    );
  }

  cerrarCredencial(): void {
    this.credencialEmitida = null;
    this.copiada = false;
  }

  revocar(estacion: Estacion): void {
    this.msg.questionCall(
      `¿Revocar la estación ${estacion.nombre}? El equipo dejará de registrar de inmediato.`,
      null,
      (respuesta: any) => {
        if (!respuesta?.value) {
          return;
        }
        this.asistenciaService.revocarEstacion(estacion).subscribe({
          next: () => {
            this.msg.successCall("La estación quedó revocada.", null, null);
            this.consultar();
          },
          error: () => this.msg.errorCall("No se pudo revocar la estación.", null, null),
        });
      }
    );
  }

  nombreUbicacion(idUbicacion: number): string {
    const sitio = this.ubicaciones.find((u) => u.id === idUbicacion);
    return sitio ? sitio.nombre : `Sitio ${idUbicacion}`;
  }

  claseEstatus(estatus: string): string {
    // Modificadores globales de table-page__status-badge (styles.css).
    return estatus === "ACTIVO"
      ? "table-page__status-badge is-active"
      : "table-page__status-badge is-inactive";
  }

  esActiva(estacion: Estacion): boolean {
    return estacion.estatus === "ACTIVO";
  }

  /**
   * "yyyy-MM-dd HH:mm", o "Nunca" si el equipo no se ha comunicado. El
   * backend manda la fecha cruda ("2026-09-30T10:15:22"); aquí la hora sí
   * importa, porque dice hace cuánto que el kiosco no se comunica.
   */
  ultimaSincronizacion(estacion: Estacion): string {
    const valor = estacion?.ultimaSincronizacion;
    return valor ? `${soloFecha(valor)} ${soloHora(valor)}` : "Nunca";
  }

  // ------------------------------------------------------------------

  private cargarUbicaciones(): void {
    this.asistenciaService.consultarUbicaciones(null).subscribe({
      next: (respuesta) => {
        // Solo tiene sentido emitir contra sitios donde haya kiosco.
        this.ubicaciones = (respuesta?.list ?? []).filter(
          (sitio) => sitio.canalRegistro === "KIOSCO" || sitio.canalRegistro === "AMBOS"
        );
        this.cdr.detectChanges();
      },
      error: () => {
        this.ubicaciones = [];
      },
    });
  }
}
